import { useEffect } from 'react';
import { apiClient } from '@/api/client';
import { useAuthStore } from '@/store/authStore';

interface TenantMetaResponse {
  currency: string;
  timezone: string;
  phonePrefix: string;
}

export function useTenantMeta() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const role = useAuthStore((s) => s.role);
  const tenantId = useAuthStore((s) => s.tenantId);
  const impersonatedTenantId = useAuthStore((s) => s.impersonatedTenantId);
  const setTenantMeta = useAuthStore((s) => s.setTenantMeta);
  const tenantCurrency = useAuthStore((s) => s.tenantCurrency);
  const activeTenantId = role === 'superadmin' ? impersonatedTenantId : tenantId;

  useEffect(() => {
    // A global SuperAdmin has no tenant context. Fetching /tenants/meta in
    // that state creates a guaranteed 404 and can cache the wrong defaults
    // before the admin chooses a tenant to impersonate.
    if (!isAuthenticated || activeTenantId == null) return;
    // Skip if already loaded
    if (tenantCurrency) return;

    apiClient
      .get<TenantMetaResponse>('/tenants/meta')
      .then((res) => {
        const { currency, timezone, phonePrefix } = res.data;
        setTenantMeta(currency || 'SDG', timezone || 'Africa/Khartoum', phonePrefix || '+249');
      })
      .catch(() => {
        // Fallback to defaults
        setTenantMeta('SDG', 'Africa/Khartoum', '+249');
      });
  }, [isAuthenticated, activeTenantId, tenantCurrency, setTenantMeta]);
}

// Non-hook accessors for use in format utils
export function getTenantCurrency(): string {
  return useAuthStore.getState().tenantCurrency ?? 'SDG';
}

export function getTenantTimezone(): string {
  return useAuthStore.getState().tenantTimezone ?? 'Africa/Khartoum';
}

export function getTenantPhonePrefix(): string {
  return useAuthStore.getState().tenantPhonePrefix ?? '+249';
}
